import React, { useEffect, useMemo, useRef, useState } from "react";
import Selecto from "react-selecto";

import { RegistryItem } from "../types";
import { useUnifiedRegistry } from "../unified-registry-context";

import { useDragDrop } from "./DragDropProvider";

import { isModifierKeyPressed } from "@/utils/shortcuts";
import { cn } from "@/lib/utils";

interface SelectionContainerProps {
  children: React.ReactNode;
  items: RegistryItem[];
  disabled?: boolean;
  className?: string;
}

const SelectionContainer: React.FC<SelectionContainerProps> = ({
  children,
  items,
  disabled = false,
  className,
}) => {
  const registry = useUnifiedRegistry();
  const { isDragging } = useDragDrop();
  const selectoRef = useRef<Selecto>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(false);

  const selectableTargets = useMemo(
    () => items.map(item => `[data-item-id="${item.id}"][data-item-type="${item.type}"]`),
    [items],
  );

  const isRadixUIOpen = () =>
    document.querySelector("[data-radix-portal]") !== null ||
    document.querySelector('[role="dialog"]') !== null ||
    document.querySelector('[role="menu"]') !== null ||
    document.querySelector('[role="menubar"]') !== null ||
    document.querySelector('[role="listbox"]') !== null ||
    document.querySelector("[data-radix-popover-content]") !== null ||
    document.querySelector("[data-radix-select-content]") !== null ||
    document.querySelector("[data-radix-dropdown-menu-content]") !== null ||
    document.querySelector("[data-radix-context-menu-content]") !== null ||
    document.querySelector("[data-radix-tooltip-content]") !== null ||
    document.querySelector("[data-radix-hover-card-content]") !== null ||
    document.querySelector("[data-radix-menubar-content]") !== null ||
    document.querySelector("[data-radix-navigation-menu-content]") !== null;

  const isSelectoDisabled = disabled || isDragging || isRadixUIOpen();

  useEffect(() => {
    // Update selecto when items change
    if (selectoRef.current && !isSelectoDisabled) {
      selectoRef.current.setSelectedTargets([]);
    }
  }, [items, isSelectoDisabled]);

  useEffect(() => {
    if (containerRef.current) {
      setIsMounted(true);
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
        return;
      }

      if (e.key === "Escape") {
        registry.clearSelection();
        if (selectoRef.current) {
          selectoRef.current.setSelectedTargets([]);
        }
        return;
      }

      if (isModifierKeyPressed(e) && e.key === "a") {
        e.preventDefault();
        registry.selectEntities(items);

        if (selectoRef.current) {
          const allTargets = selectableTargets
            .map(selector => document.querySelector(selector))
            .filter(Boolean) as HTMLElement[];
          selectoRef.current.setSelectedTargets(allTargets);
        }
        return;
      }

      if (isModifierKeyPressed(e) && e.key === "d") {
        e.preventDefault();
        registry.clearSelection();
        if (selectoRef.current) {
          selectoRef.current.setSelectedTargets([]);
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [registry, items, selectableTargets]);

  // Sync selecto selection with registry state
  useEffect(() => {
    if (!selectoRef.current || isSelectoDisabled) {
      return;
    }

    const selectedElements = registry.selectedEntities
      .map(entity =>
        document.querySelector(`[data-item-id="${entity.id}"][data-item-type="${entity.type}"]`),
      )
      .filter(Boolean) as HTMLElement[];

    selectoRef.current.setSelectedTargets(selectedElements);
  }, [registry.selectedEntities, isSelectoDisabled]);

  return (
    <div className={cn("relative", className)} ref={containerRef}>
      {isMounted && containerRef.current && (
        <Selecto
          ref={selectoRef}
          container={null}
          dragContainer={containerRef.current}
          selectableTargets={selectableTargets}
          selectByClick={false}
          selectFromInside
          continueSelect
          toggleContinueSelect={["shift"]}
          keyContainer={window}
          hitRate={0}
          scrollOptions={{
            container: () => document.querySelector("[data-scroll-container]") as HTMLElement,
            threshold: 60,
            throttleTime: 30,
          }}
          onDragStart={e => {
            const target = e.inputEvent.target as HTMLElement;

            if (isRadixUIOpen()) {
              e.stop();
              return;
            }

            if (!containerRef.current?.contains(target)) {
              e.stop();
              return;
            }

            if (
              target.closest("button") ||
              target.closest("a") ||
              target.closest("[data-dropdown-trigger]") ||
              target.closest("[data-context-menu-trigger]") ||
              target.closest("[role='dialog']") ||
              target.closest("[data-radix-portal]") ||
              target.closest("[data-radix-popover-content]") ||
              target.closest("[data-radix-select-content]") ||
              target.closest("[data-radix-dropdown-menu-content]") ||
              target.closest("[data-radix-context-menu-content]") ||
              target.closest("[data-radix-tooltip-content]") ||
              target.closest("[data-radix-hover-card-content]") ||
              target.closest("[data-radix-menubar-content]") ||
              target.closest("[data-radix-navigation-menu-content]") ||
              target.closest("[role='menu']") ||
              target.closest("[role='menubar']") ||
              target.closest("[role='listbox']") ||
              target.closest("[data-registry-banner]")
            ) {
              e.stop();
              return;
            }

            document.body.classList.add("selecting");
          }}
          onDragEnd={() => {
            document.body.classList.remove("selecting");
          }}
          onSelect={e => {
            if (isRadixUIOpen()) {
              return;
            }

            const addedElements = e.added;
            const removedElements = e.removed;

            addedElements.forEach(element => {
              const itemId = element.getAttribute("data-item-id");
              const itemType = element.getAttribute("data-item-type");

              if (itemId && itemType) {
                const foundItem = items.find(i => String(i.id) === itemId && i.type === itemType);
                if (foundItem) {
                  registry.selectEntity(foundItem);
                }
              }
            });

            removedElements.forEach(element => {
              const itemId = element.getAttribute("data-item-id");
              const itemType = element.getAttribute("data-item-type");

              if (itemId && itemType) {
                const foundItem = items.find(i => String(i.id) === itemId && i.type === itemType);
                if (foundItem) {
                  registry.deselectEntity(foundItem);
                }
              }
            });
          }}
        />
      )}
      {children}
    </div>
  );
};

export default SelectionContainer;
